import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Button } from "@mui/material";
import useFetch from '../../hooks/useFetch';
import DashboardSidebar from '../DashboardSidebar';
import './StorePayments.css';

interface EditPaymentSettingsPageProps {
  storeId?: number;
  inline?: boolean;
  initialData?: any;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const getFormDataFromData = (data: any) => {
  const cd = data?.check_details;
  return {
    store_business_email: data?.store_business_email || '',
    firstname: cd?.firstname || '',
    lastname: cd?.lastname || '',
    email: cd?.email || '',
    phone: cd?.phone || '',
    fullname: cd?.fullname || '',
    address1: cd?.address1 || '',
    address2: cd?.address2 || '',
    city: cd?.city || '',
    state: cd?.state || '',
    country: cd?.country || 'United States',
    zip_code: cd?.zip_code || ''
  };
};

const EditPaymentSettingsPage: React.FC<EditPaymentSettingsPageProps> = ({
  storeId,
  inline = false,
  initialData,
  onCancel,
  onSuccess
}) => {
  const { store_id } = useParams<{ store_id: string }>();
  const navigate = useNavigate();
  const userStr = localStorage.getItem("user");
  const userObj = userStr ? JSON.parse(userStr) : null;
  const targetStoreId = inline ? storeId : (store_id ? Number(store_id) : userObj?.store_id);

  const [paymentMethod, setPaymentMethod] = useState(initialData?.store_payment_method || 'check');
  const [formData, setFormData] = useState(() => getFormDataFromData(initialData));
  const [validationError, setValidationError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showApiError, setShowApiError] = useState(false);

  const { fetchData: fetchPaymentInfo, isLoading: isFetchingInfo, errorContent: fetchErrorContent } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/payments/store-payment-info/${targetStoreId}`,
    method: 'GET',
  });

  const { fetchData, isLoading, errorContent, error } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/payments/store-payment-settings`,
    method: 'POST',
  });

  useEffect(() => {
    // Clear any messages when edit mode state changes (for inline usage)
    setSuccessMessage('');
    setValidationError('');
    setShowApiError(false);
  }, [inline]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);


  useEffect(() => {
    if (errorContent) {
      setShowApiError(true);
      const timer = setTimeout(() => setShowApiError(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorContent]);

  useEffect(() => {
    if (initialData) {
      setPaymentMethod(initialData.store_payment_method || 'check');
      setFormData(getFormDataFromData(initialData));
    }
  }, [initialData]);

  useEffect(() => {
    if (targetStoreId && !initialData) {
      fetchPaymentInfo().then(result => {
        if (result?.success && result.data) {
          const parsed = result.data;
          setPaymentMethod(parsed.store_payment_method || 'check');
          setFormData(getFormDataFromData(parsed));
        }
      });
    }
  }, [targetStoreId, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationError) setValidationError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setValidationError('');
    
    if (!targetStoreId) return;

    // Frontend validation matching backend requirements for 'check'
    if (paymentMethod === 'check') {
      const requiredFields = [
        { key: 'fullname', label: 'Check payable to (fullname)' },
        { key: 'address1', label: 'House Number and Street (address1)' },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'country', label: 'Country' },
        { key: 'zip_code', label: 'Zip Code' },
        { key: 'firstname', label: 'First Name' },
        { key: 'lastname', label: 'Last Name' },
        { key: 'phone', label: 'Phone Number' },
        { key: 'email', label: 'Email' }
      ];

      for (const field of requiredFields) {
        if (!formData[field.key as keyof typeof formData]?.trim()) {
          setValidationError(`${field.label} is required.`);
          return;
        }
      }
    }

    const payload: any = {
      store_id: targetStoreId,
      payment_method: paymentMethod
    };

    // Do not send empty fields in the request body
    Object.entries(formData).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim() !== '') {
        payload[key] = value.trim();
      }
    });

    const result = await fetchData(payload);

    if (result?.success) {
      setSuccessMessage('Payment details updated successfully!');
      window.dispatchEvent(new CustomEvent('paymentSettingsUpdated', { detail: result.data }));
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const handleCancelClick = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(-1);
    }
  };

  const renderFormContent = () => {
    if (inline) {
      return (
        <form onSubmit={handleSubmit}>
          <div className="inline-form-grid">
            <div className="inline-form-field span-2">
              <label>Check payable to*</label>
              <input type="text" name="fullname" value={formData.fullname} onChange={handleChange} />
            </div>
            <div className="inline-form-field span-1">
              <label>First Name*</label>
              <input type="text" name="firstname" value={formData.firstname} onChange={handleChange} />
            </div>
            <div className="inline-form-field span-1">
              <label>Last Name*</label>
              <input type="text" name="lastname" value={formData.lastname} onChange={handleChange} />
            </div>

            <div className="inline-form-field span-2">
              <label>House Number and Street*</label>
              <input type="text" name="address1" value={formData.address1} onChange={handleChange} />
            </div>
            <div className="inline-form-field span-2">
              <label>Second address line</label>
              <input type="text" name="address2" value={formData.address2} onChange={handleChange} />
            </div>

            <div className="inline-form-field span-1">
              <label>City*</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange} />
            </div>
            <div className="inline-form-field span-1">
              <label>State*</label>
              <input type="text" name="state" value={formData.state} onChange={handleChange} />
            </div>
            <div className="inline-form-field span-1">
              <label>Zip Code*</label>
              <input type="text" name="zip_code" value={formData.zip_code} onChange={handleChange} />
            </div>
            <div className="inline-form-field span-1">
              <label>Country</label>
              <div className="locked-input-wrapper">
                <input 
                  type="text" 
                  name="country" 
                  value={formData.country || 'United States'} 
                  readOnly 
                  disabled
                  className="locked-input" 
                />
                <LockOutlinedIcon className="locked-input-icon" />
              </div>
            </div>

            <div className="inline-form-field span-2">
              <label>Email*</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} />
            </div>
            <div className="inline-form-field span-2">
              <label>Phone Number*</label>
              <input type="text" name="phone" value={formData.phone} onChange={handleChange} />
            </div>
          </div>

            <div className="inline-form-actions">
            {showApiError && errorContent && (
              <div className="inline-form-msg inline-form-msg--error">
                {error?.code === 401 ? errorContent : error?.message?.replace(/^Error in \w+:\s*/i, "")}
              </div>
            )}
            {validationError && (
              <div className="inline-form-msg inline-form-msg--error">
                {validationError}
                <button type="button" className="inline-form-msg-dismiss" onClick={() => setValidationError('')} aria-label="Dismiss">&times;</button>
              </div>
            )}
            {successMessage && (
              <div className="inline-form-msg">{successMessage}</div>
            )}
            <Button 
              variant="outlined"
              onClick={handleCancelClick}
              disabled={isLoading}
              className="payouts-make-payment-btn inline-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              disableElevation
              type="submit" 
              disabled={isLoading}
              className="payouts-make-payment-btn inline-save-btn"
            >
              {isLoading ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </form>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="payouts-settings-form">
                <div>
                  <label className="payouts-settings-label-main">Payment Method (Check)</label>
                  <div className="payouts-payment-method-group">
                    {/* Payment method text only, no radio button */}
                  </div>
                </div>

                <div className="payouts-settings-grid">
                  
                  {/* Column 1 */}
                  <div className="payouts-settings-col">
                    <div className="payouts-settings-field">
                      <label>Check payable to*</label>
                      <input type="text" name="fullname" value={formData.fullname} onChange={handleChange} className="settings-input" />
                    </div>
                    <div className="payouts-settings-field">
                      <label>House Number and Street*</label>
                      <input type="text" name="address1" value={formData.address1} onChange={handleChange} className="settings-input" />
                    </div>
                    <div className="payouts-settings-field">
                      <label>Second address line</label>
                      <input type="text" name="address2" value={formData.address2} onChange={handleChange} className="settings-input" />
                    </div>
                    <div className="payouts-settings-field">
                      <label>City*</label>
                      <input type="text" name="city" value={formData.city} onChange={handleChange} className="settings-input" />
                    </div>
                    <div className="payouts-settings-field">
                      <label>State*</label>
                      <input type="text" name="state" value={formData.state} onChange={handleChange} className="settings-input" />
                    </div>
                    <div className="payouts-settings-field">
                      <label>Country</label>
                      <div className="locked-input-wrapper">
                        <input 
                          type="text" 
                          name="country" 
                          value={formData.country || 'United States'} 
                          readOnly 
                          disabled
                          className="settings-input locked-input" 
                        />
                        <LockOutlinedIcon className="locked-input-icon" />
                      </div>
                    </div>
                    <div className="payouts-settings-field">
                      <label>Zip Code*</label>
                      <input type="text" name="zip_code" value={formData.zip_code} onChange={handleChange} className="settings-input" />
                    </div>
                  </div>
                  
                  {/* Column 2 */}
                  <div className="payouts-settings-col">
                    <div className="payouts-settings-field">
                      <label>First Name*</label>
                      <input type="text" name="firstname" value={formData.firstname} onChange={handleChange} className="settings-input" />
                    </div>
                    <div className="payouts-settings-field">
                      <label>Last Name*</label>
                      <input type="text" name="lastname" value={formData.lastname} onChange={handleChange} className="settings-input" />
                    </div>
                    <div className="payouts-settings-field">
                      <label>Phone Number*</label>
                      <input type="text" name="phone" value={formData.phone} onChange={handleChange} className="settings-input" />
                    </div>
                    <div className="payouts-settings-field">
                      <label>Email*</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} className="settings-input" />
                    </div>
                  </div>

                </div>

                <div className="payouts-settings-actions payouts-settings-actions-container">
                  {showApiError && errorContent && (
                    <div className="payouts-settings-message-text payouts-settings-message-text--error">
                      {error?.code === 401 ? errorContent : error?.message?.replace(/^Error in \w+:\s*/i, "")}
                    </div>
                  )}
                  {validationError && (
                    <div className="payouts-settings-message-text payouts-settings-message-text--error">
                      {validationError}
                      <button type="button" className="inline-form-msg-dismiss" onClick={() => setValidationError('')} aria-label="Dismiss">&times;</button>
                    </div>
                  )}
                  {successMessage && (
                    <div className="payouts-settings-message-text">
                      {successMessage}
                    </div>
                  )}
          <div className="payouts-settings-btn-group">
            <button 
              type="button" 
              onClick={handleCancelClick}
              disabled={isLoading}
              className="make-payment-btn make-payment-cancel-btn"
            >
              Cancel
            </button>
                  <button type="submit" disabled={isLoading} className="make-payment-btn payouts-save-btn">
                    {isLoading ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
                </div>
              </form>
    );
  };

  if (inline) {
    return isFetchingInfo && !initialData ? (
      <div className="payment-info-loading">
        <p>Loading current payment info...</p>
      </div>
    ) : (
      renderFormContent()
    );
  }

  return (
    <>
    <div className="payouts-page-wrapper">
      {userObj?.current_role !== 1 && <DashboardSidebar />}
      <div className="payouts-content">
        <div className="div-block-28">
          <div className="payouts-toolbar">
            <div className="payouts-toolbar-row">
              {userObj?.current_role === 1 && (
                <span onClick={handleCancelClick} className="payouts-back-btn">
                  <ArrowBackIcon />
                </span>
              )}
              <span className="payment-details-title">Edit Payment Settings</span>
            </div>
          </div>
          
          {fetchErrorContent ? (
            <div className="payouts-table-wrapper">
              <div className="error-alert">
                {fetchErrorContent}
              </div>
            </div>
          ) : (
            <div className="payouts-table-wrapper payouts-table-wrapper-centered">
              <div className="payment-info-container">
                {isFetchingInfo && !initialData ? (
                  <div className="payment-info-loading">
                    <p>Loading current payment info...</p>
                  </div>
                ) : (
                  renderFormContent()
                )}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default EditPaymentSettingsPage;
